<template>
<div :class="$style.container">
    <div :class="$style.main">
        <h1>{{ $t("home.title") }}</h1>
        <p>{{ $t("home.desc") }}</p>
        <div :class="$style.button_container">
            <app-button
                color="primary"
                :label="$t('home.appConsoleShortcut')"
                @click.native="onClick" />
            <app-button
                color="primary"
                :label="$t('home.appConsoleRequest')"
                @click.native="onClickRequest" />
        </div>
        <m-button
            :class="$style.notice_btn"
            :label="$t('home.noticeShortcut')"
            leading-icon="ic_notice"
            outlined
            @click.native="onClickNotice" />
    </div>
    <div :class="$style.links_items">
        <component
            :is="href ? 'a' : 'router-link'"
            v-for="({ title, desc, href, to, key, isEnd }, index) in items"
            :key="`home-link-${index}`"
            :class="$style.item"
            :href="href"
            :to="to"
            :target="href ? '_blank' : null"
            @click="$nc($event, `hme.${key}`)"
            @click.native="$nc($event, `hme.${key}`)">
            <svg-icon :name="`icon_${key}`" />
            <h2>{{ title }}</h2>
            <p>{{ desc }}</p>
            <div
                v-if="isEnd"
                :class="$style.end">
                {{ $t("home.obsolete") }}
            </div>
        </component>
    </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AppButton from "@/components/AppButton.vue";
import mButton from "@/components/mdc/Button.vue";
import SvgIcon from "@/components/SvgIcon.vue";
import { whale_lab } from "@/data/links";
import { APP_CONSOLE } from "@/router/path";

export default {
    components: { AppButton, SvgIcon, mButton },
    data() {
        const text = this.$t("home.items");
        return {
            items: [
                { ...text.guide, key: "guide", to: "/guide", isEnd: true },
                {
                    ...text.api,
                    key: "api",
                    to: "/guide/login-api",
                    isEnd: true
                },
                { ...text.lab, key: "lab", href: whale_lab, isEnd: false }
            ]
        };
    },
    computed: {
        ...mapGetters(["isDeveloper", "isLoggedIn"]),
        loginFailed() {
            return (
                !this.isLoggedIn &&
                this.$route.query?.error === "INVALID_LOGIN_INFO"
            );
        }
    },
    watch: {
        loginFailed: {
            immediate: true,
            handler(loginFailed) {
                if (!loginFailed) return;
                this.UPDATE_DIALOG({
                    title: this.$t("invalidLoginInfo.title"),
                    slot: () =>
                        import(
                            "@/components/dialogs/user/Dialog.invalidLoginInfo.vue"
                        ),
                    actions: [
                        {
                            action: "confirm",
                            label: this.$t("label.confirm"),
                            autofocus: true
                        }
                    ]
                });
            }
        }
    },
    methods: {
        ...mapActions(["UPDATE_DIALOG"]),
        onClick() {
            window.open("https://apps.worksmobile.com/");
        },
        onClickRequest() {
            window.open(`${APP_CONSOLE}?hl=ko`);
        },
        onClickNotice() {
            window.open("https://whalespace.io/notices/16943");
        }
    }
};
</script>

<style lang="scss" module>
@use "@material/button";
@use "@material/ripple";
@use "@material/typography/typography";
@use "@/assets/scss/mixins" as m;

.container {
    width: 100%;
    @include m.inner(0, 1380px);
    @include m.m_inner;
    .main {
        background-color: #5472cc;
        color: #fff;
        height: 450px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        border-radius: 10px;
        background-image: url("~@/assets/img/visual.png");
        background-repeat: no-repeat;
        background-size: 100% auto;
        background-position: center;
        @include m.lg {
            background-image: url("~@/assets/img/visual_tablet.png");
            background-size: 100% auto;
        }
        .notice_btn {
            @include button.outline-color(#ffffff33);
            @include button.container-fill-color(rgba(0, 0, 0, 0));
            @include button.ink-color(#fff);
            font-size: typography.px-to-rem(13px);
            margin-top: 18px;
        }
        .button_container {
            display: flex;
            gap: 16px;
            flex-direction: row;
            margin-top: 28px;
            :global {
                .mdc-button {
                    font-size: typography.px-to-rem(16px);
                    @include button.ink-color(#fff);
                    @include button.container-fill-color(#385bc2);
                    @include button.horizontal-padding(20px);
                    @include button.height(50px);
                    .mdc-button__ripple {
                        @include ripple.states-base-color(black);
                    }
                }
            }
        }
        h1 {
            font-size: typography.px-to-rem(34px);
            white-space: pre;
            @include m.md {
                white-space: pre-line;
            }
        }
        p {
            white-space: pre-line;
            font-size: typography.px-to-rem(14px);
            line-height: 1.5;
            margin: 0;
            margin-top: 25px;
            @include m.md {
                white-space: normal;
                max-width: 264px;
                word-break: keep-all;
            }
        }
    }
    .links_items {
        display: flex;
        gap: 40px;
        margin-top: 50px;
        @include m.lg {
            gap: 20px;
            flex-direction: column;
        }
        .item {
            flex: 1;
            border: 1px solid #e5e5e5;
            border-radius: 10px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 30px 0px;
            text-align: center;
            color: #000;
            position: relative;
            &:hover {
                box-shadow: 0 4px 12px 0 rgb(0 0 0 / 12%);
            }
            svg {
                width: 80px;
                height: 80px;
                margin-bottom: 20px;
            }
            h2 {
                margin: 0;
                font-size: typography.px-to-rem(20px);
            }
            p {
                margin: 8px 0 0;
                white-space: pre-line;
                font-size: typography.px-to-rem(15px);
            }
            .end {
                color: #fff;
                background: #ff5449;
                border-radius: 18px;
                padding: 2px 8px;
                font-size: typography.px-to-rem(13px);
                position: absolute;
                right: 10px;
                top: 10px;
            }
        }
    }
}
</style>
